import { Component, Input } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { isUploadFilePreviewable, encodeFilePreviewUrl } from 'src/app/shared/helpers/upload-file-preview.util';
import { FloatingUploadPreviewService } from 'src/app/shared/service/floating-upload-preview/floating-upload-preview.service';
import {
  formatOnboardingModuleLabel,
  getOnboardingModuleBadgeClass,
} from 'src/app/shared/helpers/onboarding-modules.util';
import { normalizeImoDigits } from '../onboarding-vessel-mapper.util';
import {
  buildCargoOnboardingDashboardView,
  CargoOnboardingDashboardView,
  CargoOnboardingKvSection,
  CargoOnboardingTableSection,
  CargoOnboardingUploadedFile,
  formatUploadedFileSize,
  isPreviewableUploadedFileUrl,
} from '../cargo-heating-onboarding-view.util';

@Component({
  selector: 'app-cargo-heating-onboarding-dashboard',
  templateUrl: './cargo-heating-onboarding-dashboard.component.html',
  styleUrls: ['./cargo-heating-onboarding-dashboard.component.scss'],
})
export class CargoHeatingOnboardingDashboardComponent {
  @Input() detail: Record<string, unknown> | null = null;
  @Input() selectedImo = '';
  @Input() loading = false;
  @Input() isDarkMode = false;

  constructor(
    private floatingPreview: FloatingUploadPreviewService,
    private toastr: ToastrService,
  ) {}

  get view(): CargoOnboardingDashboardView | null {
    if (!this.detail || !this.matchesSelectedImo) {
      return null;
    }
    return buildCargoOnboardingDashboardView(this.detail);
  }

  get matchesSelectedImo(): boolean {
    if (!this.detail || !this.selectedImo) {
      return false;
    }
    const detailImo = normalizeImoDigits(
      String(this.detail['imo'] ?? this.detail['vessel_imo'] ?? ''),
    );
    return detailImo === normalizeImoDigits(this.selectedImo);
  }

  trackSection(_index: number, section: CargoOnboardingKvSection): string {
    return section.id;
  }

  trackTableSection(_index: number, section: CargoOnboardingTableSection): string {
    return section.id;
  }

  trackFile(_index: number, file: CargoOnboardingUploadedFile): string {
    return `${file.field}-${file.fileName}-${file.s3Url}`;
  }

  formatFileSize(bytes?: number): string {
    return formatUploadedFileSize(bytes);
  }

  formatModuleLabel(moduleKey: string): string {
    return formatOnboardingModuleLabel(moduleKey);
  }

  moduleBadgeClass(moduleKey: string): string {
    return getOnboardingModuleBadgeClass(moduleKey);
  }

  trackModuleKey(_index: number, moduleKey: string): string {
    return moduleKey;
  }

  canPreviewFile(file: CargoOnboardingUploadedFile): boolean {
    return (
      isPreviewableUploadedFileUrl(file.s3Url) &&
      isUploadFilePreviewable(file.fileName)
    );
  }

  canDownloadFile(file: CargoOnboardingUploadedFile): boolean {
    return isPreviewableUploadedFileUrl(file.s3Url);
  }

  previewFile(file: CargoOnboardingUploadedFile, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.canPreviewFile(file)) {
      this.toastr.info('Preview URL not available for this file.', 'Preview');
      return;
    }

    this.floatingPreview.open({
      fileName: file.fileName,
      previewUrl: file.s3Url,
    });
  }

  downloadFile(file: CargoOnboardingUploadedFile, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.canDownloadFile(file)) {
      this.toastr.info('Download URL not available for this file.', 'Download');
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = encodeFilePreviewUrl(file.s3Url);
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.download = file.fileName || 'download';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }
}
